import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Users,
  UserPlus,
  Search,
  Filter,
  Eye,
  Edit2,
  UserX,
  UserCheck,
  CheckCircle2,
  AlertCircle,
  X,
  Plus,
  Trash2,
  BookOpen,
  Phone,
  Mail,
  Shield,
  Layers
} from 'lucide-react';

interface TeacherAssignment {
  id?: number;
  board_id: number;
  class_id: number;
  section: string;
  subject_id: number;
  board_name?: string;
  board_code?: string;
  class_name?: string;
  subject_name?: string;
}

interface Teacher {
  id: number;
  teacher_name: string;
  mobile: string;
  email?: string;
  designation: string;
  status: 'active' | 'inactive';
  username?: string;
  assignments: TeacherAssignment[];
}

export const TeachersManagementPage: React.FC = () => {
  const { fetchWithAuth } = useAuth();
  const { t } = useLanguage();

  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [filterBoard, setFilterBoard] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Master options
  const [boards, setBoards] = useState<Array<{ id: number; name: string; short_name: string }>>([]);
  const [classes, setClasses] = useState<Array<{ id: number; name: string }>>([]);
  const [subjects, setSubjects] = useState<Array<{ id: number; name: string; code: string }>>([]);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    teacher_name: '',
    mobile: '',
    email: '',
    designation: 'PGT',
    username: '',
    password: '',
    assignments: [] as TeacherAssignment[]
  });

  const loadData = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterStatus) params.append('status', filterStatus);
      if (filterBoard) params.append('board_id', filterBoard);
      if (filterClass) params.append('class_id', filterClass);
      if (filterSubject) params.append('subject_id', filterSubject);

      const [teachersRes, boardsRes, classesRes, subjectsRes] = await Promise.all([
        fetchWithAuth(`/api/teachers?${params.toString()}`),
        fetchWithAuth('/api/boards'),
        fetchWithAuth('/api/classes'),
        fetchWithAuth('/api/subjects')
      ]);

      const [tData, bData, cData, sData] = await Promise.all([
        teachersRes.json(),
        boardsRes.json(),
        classesRes.json(),
        subjectsRes.json()
      ]);

      if (tData.success) setTeachers(tData.teachers);
      if (bData.success) setBoards(bData.boards);
      if (cData.success) setClasses(cData.classes);
      if (sData.success) setSubjects(sData.subjects);
    } catch (err) {
      console.error('Failed to load teachers data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filterBoard, filterClass, filterSubject, filterStatus]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadData();
  };

  const handleResetFilters = () => {
    setSearch('');
    setFilterBoard('');
    setFilterClass('');
    setFilterSubject('');
    setFilterStatus('all');
    // will trigger loadData via useEffect
  };

  const openAddModal = () => {
    setFormData({
      teacher_name: '',
      mobile: '',
      email: '',
      designation: 'PGT',
      username: '',
      password: 'teacher123',
      assignments: [
        {
          board_id: boards[0]?.id || 1,
          class_id: classes[0]?.id || 1,
          section: 'A',
          subject_id: subjects[0]?.id || 1
        }
      ]
    });
    setMessage(null);
    setShowAddModal(true);
  };

  const openEditModal = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setFormData({
      teacher_name: teacher.teacher_name,
      mobile: teacher.mobile,
      email: teacher.email || '',
      designation: teacher.designation,
      username: teacher.username || '',
      password: '',
      assignments: teacher.assignments.length > 0
        ? teacher.assignments.map(a => ({
            board_id: a.board_id,
            class_id: a.class_id,
            section: a.section || 'A',
            subject_id: a.subject_id
          }))
        : [
            {
              board_id: boards[0]?.id || 1,
              class_id: classes[0]?.id || 1,
              section: 'A',
              subject_id: subjects[0]?.id || 1
            }
          ]
    });
    setMessage(null);
    setShowEditModal(true);
  };

  const openViewModal = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setShowViewModal(true);
  };

  const openToggleStatusConfirm = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setShowConfirmModal(true);
  };

  const addAssignmentRow = () => {
    setFormData({
      ...formData,
      assignments: [
        ...formData.assignments,
        {
          board_id: boards[0]?.id || 1,
          class_id: classes[0]?.id || 1,
          section: 'A',
          subject_id: subjects[0]?.id || 1
        }
      ]
    });
  };

  const removeAssignmentRow = (index: number) => {
    const updated = formData.assignments.filter((_, i) => i !== index);
    setFormData({ ...formData, assignments: updated });
  };

  const handleAssignmentChange = (index: number, field: keyof TeacherAssignment, value: any) => {
    const updated = [...formData.assignments];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, assignments: updated });
  };

  const handleSaveAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.teacher_name.trim() || !formData.mobile.trim() || !formData.designation.trim()) {
      setMessage({ type: 'error', text: 'Name, mobile number, and designation are required.' });
      return;
    }
    if (!/^[6-9]\d{9}$/.test(formData.mobile.trim())) {
      setMessage({ type: 'error', text: 'Please enter a valid 10-digit mobile number starting with 6-9.' });
      return;
    }

    setSaving(true);
    try {
      const res = await fetchWithAuth('/api/teachers', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setShowAddModal(false);
        setMessage({ type: 'success', text: data.message });
        loadData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to add teacher.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server error while adding teacher.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeacher) return;

    if (!formData.teacher_name.trim() || !formData.mobile.trim() || !formData.designation.trim()) {
      setMessage({ type: 'error', text: 'Name, mobile number, and designation are required.' });
      return;
    }
    if (!/^[6-9]\d{9}$/.test(formData.mobile.trim())) {
      setMessage({ type: 'error', text: 'Please enter a valid 10-digit mobile number.' });
      return;
    }

    setSaving(true);
    try {
      const res = await fetchWithAuth(`/api/teachers/${selectedTeacher.id}`, {
        method: 'PUT',
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setShowEditModal(false);
        setMessage({ type: 'success', text: 'Teacher updated successfully.' });
        loadData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update teacher.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server error while updating teacher.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedTeacher) return;
    setSaving(true);
    try {
      const res = await fetchWithAuth(`/api/teachers/${selectedTeacher.id}/toggle-status`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        setShowConfirmModal(false);
        setMessage({ type: 'success', text: data.message });
        loadData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Status change failed.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server error.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-[#0F2C59] text-white rounded-xl shadow-xs">
            <Users className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">
              {t('nav_teachers')}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Faculty registry, role credentials, board & class assignments for District Nuh
            </p>
          </div>
        </div>

        <button
          onClick={openAddModal}
          className="bg-[#0F2C59] hover:bg-[#081B38] text-white text-xs font-bold py-2.5 px-4 rounded-lg shadow-sm flex items-center justify-center space-x-2 transition"
        >
          <UserPlus className="w-4 h-4 text-amber-400" />
          <span>{t('addTeacher')}</span>
        </button>
      </div>

      {/* Global Alert Notification */}
      {message && (
        <div
          className={`p-4 rounded-xl text-xs flex items-center justify-between border ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
              : 'bg-rose-50 text-rose-800 border-rose-300'
          }`}
        >
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span className="font-semibold">{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search & Filter Bar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-4 space-y-3">
        <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          {/* Search box */}
          <div className="lg:col-span-2 relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by teacher name or mobile..."
              className="w-full pl-9 pr-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 text-slate-900"
            />
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          </div>

          {/* Filter Board */}
          <div>
            <select
              value={filterBoard}
              onChange={(e) => setFilterBoard(e.target.value)}
              className="w-full px-2.5 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 text-slate-900"
            >
              <option value="">All Boards (सभी बोर्ड)</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.short_name}
                </option>
              ))}
            </select>
          </div>

          {/* Filter Class */}
          <div>
            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              className="w-full px-2.5 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 text-slate-900"
            >
              <option value="">All Classes (सभी कक्षाएं)</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Filter Status */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-2.5 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 text-slate-900"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {/* Action buttons */}
          <div className="flex space-x-2">
            <button
              type="submit"
              className="flex-1 bg-[#0F2C59] hover:bg-[#081B38] text-white text-xs font-bold py-2 px-3 rounded-lg shadow-xs transition"
            >
              {t('filter')}
            </button>
            <button
              type="button"
              onClick={handleResetFilters}
              className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-semibold py-2 px-3 rounded-lg transition"
            >
              {t('reset')}
            </button>
          </div>
        </form>
      </div>

      {/* Teachers Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-[#0F2C59] text-white uppercase tracking-wider text-[11px] font-semibold">
                <th className="py-3 px-4">Teacher</th>
                <th className="py-3 px-4">Designation</th>
                <th className="py-3 px-4">Assigned Subjects</th>
                <th className="py-3 px-4">Board</th>
                <th className="py-3 px-4">Classes & Sec</th>
                <th className="py-3 px-4">Mobile</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-slate-500 font-medium">
                    {t('loading')}
                  </td>
                </tr>
              ) : teachers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-slate-500 font-medium">
                    {t('noData')}
                  </td>
                </tr>
              ) : (
                teachers.map((teacher) => {
                  const subjectNames = Array.from(new Set(teacher.assignments.map(a => a.subject_name))).filter(Boolean);
                  const boardCodes = Array.from(new Set(teacher.assignments.map(a => a.board_code))).filter(Boolean);
                  const classSecs = teacher.assignments.map(a => `${a.class_name || 'Class'} (${a.section})`);

                  return (
                    <tr key={teacher.id} className="hover:bg-slate-50 transition">
                      {/* Teacher name & username */}
                      <td className="py-3 px-4">
                        <div className="font-bold text-slate-900 text-sm">
                          {teacher.teacher_name}
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono">
                          @{teacher.username || 'n/a'}
                        </div>
                      </td>

                      {/* Designation */}
                      <td className="py-3 px-4 font-medium text-slate-700">
                        {teacher.designation}
                      </td>

                      {/* Subjects */}
                      <td className="py-3 px-4">
                        {subjectNames.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {subjectNames.map((s, idx) => (
                              <span key={idx} className="bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 rounded text-[10px] font-semibold">
                                {s}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">None assigned</span>
                        )}
                      </td>

                      {/* Board */}
                      <td className="py-3 px-4">
                        {boardCodes.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {boardCodes.map((b, idx) => (
                              <span
                                key={idx}
                                className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                                  b === 'HBSE'
                                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                    : 'bg-indigo-50 text-indigo-800 border-indigo-200'
                                }`}
                              >
                                {b}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </td>

                      {/* Classes & Sections */}
                      <td className="py-3 px-4">
                        {classSecs.length > 0 ? (
                          <div className="text-slate-700 font-medium">
                            {classSecs.join(', ')}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </td>

                      {/* Mobile */}
                      <td className="py-3 px-4 font-mono text-slate-800">
                        {teacher.mobile}
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                            teacher.status === 'active'
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                              : 'bg-rose-100 text-rose-800 border-rose-300'
                          }`}
                        >
                          ● {teacher.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-1.5">
                          <button
                            onClick={() => openViewModal(teacher)}
                            title="View Profile"
                            className="p-1.5 text-slate-600 hover:text-[#0F2C59] hover:bg-slate-100 rounded transition"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(teacher)}
                            title="Edit Details"
                            className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openToggleStatusConfirm(teacher)}
                            title={teacher.status === 'active' ? 'Deactivate Teacher' : 'Activate Teacher'}
                            className={`p-1.5 rounded transition ${
                              teacher.status === 'active'
                                ? 'text-rose-600 hover:bg-rose-50'
                                : 'text-emerald-600 hover:bg-emerald-50'
                            }`}
                          >
                            {teacher.status === 'active' ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ======================================================== */}
      {/* 1. ADD / EDIT TEACHER MODAL */}
      {/* ======================================================== */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full border border-slate-300 overflow-hidden my-8">
            <div className="bg-[#0F2C59] text-white px-5 py-4 flex justify-between items-center border-b-2 border-amber-500">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-amber-400" />
                <h3 className="text-sm font-bold">
                  {showAddModal ? t('addTeacher') : t('editTeacher')}
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                className="text-slate-300 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleSaveAdd : handleSaveEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    {t('teacherName')} <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.teacher_name}
                    onChange={(e) => setFormData({ ...formData, teacher_name: e.target.value })}
                    placeholder="e.g. Sunita Yadav"
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 text-slate-900"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    {t('mobile')} <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={10}
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, '') })}
                    placeholder="10-digit phone (e.g. 9896012345)"
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 text-slate-900 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    {t('designation')} <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    placeholder="e.g. PGT Mathematics, TGT Science"
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 text-slate-900"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    {t('email')}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="e.g. sunita.yadav@gmssss.edu"
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 text-slate-900"
                  />
                </div>

                {showAddModal && (
                  <>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        Portal Login Username
                      </label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        placeholder="Leave blank to auto-generate (e.g. teacher_9896012345)"
                        className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 text-slate-900 font-mono"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        Initial Password
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Default: teacher123"
                        className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 text-slate-900"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Class & Subject Assignments Section */}
              <div className="pt-3 border-t border-slate-200">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    {t('assignedClasses')}
                  </div>
                  <button
                    type="button"
                    onClick={addAssignmentRow}
                    className="text-xs text-blue-700 hover:text-blue-900 font-bold flex items-center space-x-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>{t('addAssignment')}</span>
                  </button>
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {formData.assignments.map((assign, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 sm:grid-cols-4 gap-2 bg-slate-50 p-2.5 rounded-lg border border-slate-200 items-center text-xs"
                    >
                      {/* Board Select */}
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 mb-0.5">Board</label>
                        <select
                          value={assign.board_id}
                          onChange={(e) => handleAssignmentChange(index, 'board_id', Number(e.target.value))}
                          className="w-full p-1.5 text-xs border border-slate-300 rounded bg-white"
                        >
                          {boards.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.short_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Class Select */}
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 mb-0.5">Class</label>
                        <select
                          value={assign.class_id}
                          onChange={(e) => handleAssignmentChange(index, 'class_id', Number(e.target.value))}
                          className="w-full p-1.5 text-xs border border-slate-300 rounded bg-white"
                        >
                          {classes.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Subject Select */}
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 mb-0.5">Subject</label>
                        <select
                          value={assign.subject_id}
                          onChange={(e) => handleAssignmentChange(index, 'subject_id', Number(e.target.value))}
                          className="w-full p-1.5 text-xs border border-slate-300 rounded bg-white"
                        >
                          {subjects.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Section & Remove */}
                      <div className="flex items-center space-x-1.5">
                        <div className="flex-1">
                          <label className="block text-[10px] font-bold text-slate-500 mb-0.5">Section</label>
                          <input
                            type="text"
                            maxLength={2}
                            value={assign.section}
                            onChange={(e) => handleAssignmentChange(index, 'section', e.target.value.toUpperCase())}
                            placeholder="A"
                            className="w-full p-1.5 text-xs border border-slate-300 rounded bg-white text-center font-bold"
                          />
                        </div>
                        {formData.assignments.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAssignmentRow(index)}
                            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded mt-4"
                            title="Remove assignment"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold py-2 px-4 rounded-lg transition"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-[#0F2C59] hover:bg-[#081B38] text-white text-xs font-bold py-2 px-5 rounded-lg shadow-sm transition disabled:opacity-50"
                >
                  {saving ? t('saving') : t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 2. VIEW TEACHER PROFILE MODAL */}
      {/* ======================================================== */}
      {showViewModal && selectedTeacher && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full border border-slate-300 overflow-hidden">
            <div className="bg-[#0F2C59] text-white px-5 py-4 flex justify-between items-center border-b-2 border-amber-500">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-amber-400" />
                <h3 className="text-sm font-bold">Faculty Record</h3>
              </div>
              <button onClick={() => setShowViewModal(false)} className="text-slate-300 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="flex items-center space-x-3 pb-3 border-b border-slate-100">
                <div className="w-12 h-12 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center font-bold text-lg shadow">
                  {selectedTeacher.teacher_name.charAt(0)}
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-900">{selectedTeacher.teacher_name}</h4>
                  <div className="text-slate-500 font-medium">{selectedTeacher.designation}</div>
                  <div className="text-[11px] text-amber-700 font-mono">Username: @{selectedTeacher.username}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                  <span className="text-slate-500 font-bold block mb-0.5">Mobile Contact</span>
                  <span className="font-mono font-semibold text-slate-800">📱 {selectedTeacher.mobile}</span>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                  <span className="text-slate-500 font-bold block mb-0.5">Account Status</span>
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                      selectedTeacher.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                    }`}
                  >
                    ● {selectedTeacher.status.toUpperCase()}
                  </span>
                </div>
              </div>

              <div>
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider block mb-2">
                  Assigned Teaching Matrix
                </span>
                <div className="space-y-1.5">
                  {selectedTeacher.assignments.length === 0 ? (
                    <div className="p-3 text-slate-500 italic bg-slate-50 rounded">No assigned subjects.</div>
                  ) : (
                    selectedTeacher.assignments.map((a, i) => (
                      <div key={i} className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <span className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded font-bold text-[10px]">
                            {a.board_code || a.board_name}
                          </span>
                          <span className="font-bold text-slate-800">{a.class_name} (Sec {a.section})</span>
                        </div>
                        <span className="font-semibold text-[#0F2C59]">{a.subject_name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 flex justify-end">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="bg-slate-800 text-white text-xs font-bold py-2 px-4 rounded-lg"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 3. CONFIRM STATUS TOGGLE (DEACTIVATE / ACTIVATE) MODAL */}
      {/* ======================================================== */}
      {showConfirmModal && selectedTeacher && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-slate-300 overflow-hidden">
            <div
              className={`px-5 py-4 text-white flex justify-between items-center ${
                selectedTeacher.status === 'active' ? 'bg-rose-700' : 'bg-emerald-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-white" />
                <h3 className="text-sm font-bold">
                  {selectedTeacher.status === 'active' ? t('deactivateConfirmTitle') : t('activateConfirmTitle')}
                </h3>
              </div>
              <button onClick={() => setShowConfirmModal(false)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <p className="text-slate-700 font-medium">
                {selectedTeacher.status === 'active' ? t('deactivateConfirmMsg') : t('activateConfirmMsg')}
              </p>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div className="font-bold text-slate-900 text-sm">{selectedTeacher.teacher_name}</div>
                <div className="text-slate-500 font-mono">📱 {selectedTeacher.mobile} • @{selectedTeacher.username}</div>
              </div>

              <div className="pt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold py-2 px-4 rounded-lg"
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleToggleStatus}
                  className={`text-white text-xs font-bold py-2 px-5 rounded-lg shadow-sm transition ${
                    selectedTeacher.status === 'active'
                      ? 'bg-rose-700 hover:bg-rose-800'
                      : 'bg-emerald-700 hover:bg-emerald-800'
                  }`}
                >
                  {saving ? t('saving') : selectedTeacher.status === 'active' ? t('deactivate') : t('activate')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
